// C4 대장간 심화 계약(docs/prd/c4-forge-deep.md 5·6장). L1 장부·L2 서버 상한·L5 시뮬이 함께 따르는 저장 필드·연산·비용 공식의 정본이다.
// 순수 모듈: three·DOM·서버 import 금지. 수치 상수는 content/economy.ts(REROLL_*·ENGRAVE_*·DRILL_COST), 보석 6·7등급 코드는 content/consumables.ts.
//
// ── GearItem 선택 필드(gear.v1, 없으면 기본값 — 옛 저장 그대로) ──
//   rr?: number      재련 카운터 n(0부터, 재련 1회마다 +1, 단조 증가). 굴림 씨앗 = hashSeed('rr|' + uid + '|' + n). 우편·거래 복사에서 보존(재설정 금지)
//   lk?: number      잠근 옵션 줄 비트마스크(bit i = affixes[i]). 켜진 비트 ≤ REROLL_LOCK_MAX, i < affixes.length. 0이면 필드 없음
//   rp?: GearAffix[] 비교 대기 중인 재련 결과(affixes와 같은 길이, 잠근 줄은 같은 값). 있으면 새 재련·잠금 변경 불가 → rerollPick으로 고른다
//   eg?: number      전설 각인 단계 0..ENGRAVE_MAX(전설만). 고유 효과 배율 × (1 + ENGRAVE_EFFECT_PER_STEP × eg)
//   dr?: 1           전설 소켓 천공 완료(전설만, 장비당 1회). 소켓 수 = SOCKET_CAP[grade] + (dr ? 1 : 0) ≤ SOCKET_MAX_DRILLED
// 위 필드가 하나라도 있는 개체가 장부에 있으면 저장 버전 GEAR_VERSION_FORGE(6)로 쓴다 — 옛 빌드(≤5)는 future로 읽어 덮어쓰지 않는다.
// 재련 가루(cons.reroll_dust)와 보석 코드 15..20은 옛 빌드도 모르는 값을 보존하므로 그것만으로는 버전을 올리지 않는다.
//
// ── 연산(GearOpBody에 추가) ──
//   { t:'reroll', uid, n, sure?: AffixId, town, gold }        n = 기대 재련 카운터(개체 rr ?? 0와 같을 때만 적용, 적용 뒤 rr = n+1, rp 생성)
//   { t:'rerollPick', uid, n, take: boolean }                 n = 지금 rr(대기 결과를 만든 뒤의 값). take면 affixes ← rp, 아니면 버림. 둘 다 rp 삭제
//   { t:'rerollLock', uid, mask }                             잠금 마스크 설정(무료, 대기 결과가 있으면 거부)
//   { t:'engrave', uid, to, cycle, gold }                     to = eg+1일 때만(확정). 비용 ENGRAVE_STEPS[eg]: 최상 강화석(tier 3) + 골드
//   { t:'gemHigh', code, town, cycle, gold }                  5등급(→6, cycle ≥ GEM_HIGH.gateCycle[0]) 또는 6등급(→7, cycle ≥ [1]) 보석 3개 → 1개, 수수료 GEM_HIGH.fee
//   { t:'drill', uid, town, cycle, gold }                     전설·dr 없음일 때만(town ≥ FORGE_TOWN, cycle ≥ DRILL_COST.gateCycle). DRILL_COST 골드 + 최상 강화석
// 같은 연산 재전송·두 탭 합치기는 연산 id 기억 + 카운터(n·to) 불일치로 무시된다 → 가루·골드 이중 차감 0.
// 젬 쿠폰(각인·천공 보급)은 이 덩어리 범위 밖(L6). 재련에는 어떤 유료 재화도 쓰이지 않는다.
import { DIFFICULTY_ILVL_ADD, DIFFICULTY_LOOT, REROLL_DUST, REROLL_GOLD_K, REROLL_LOCK_MULT, REROLL_SURE, SHOP_PRICE, SHOP_TIER_PRICE_MULT } from '../content/economy';
import type { Difficulty, RegionNo } from '../content/types';

/** 장비 아이템 레벨 − 정의 레벨 → 난이도(드롭 때 DIFFICULTY_ILVL_ADD를 더했다). 모르는 차이는 노말 */
export function diffOfIlvl(defLevel: number, ilvl: number): Difficulty {
  const add = ilvl - defLevel;
  return add >= DIFFICULTY_ILVL_ADD.hell ? 'hell' : add >= DIFFICULTY_ILVL_ADD.hard ? 'hard' : 'normal';
}

export interface RerollCost { readonly gold: number; readonly dust: number }

/**
 * 재련 1회 비용(PRD 4.2): 골드 = 600 × 상점 단계 배율[지역−1] × 난이도 골드 배율 × REROLL_GOLD_K × 잠금 배율(× 확정형 2), 반올림.
 * 가루 = 잠금 줄 수별 3·5·8(× 확정형 3). locks는 0..REROLL_LOCK_MAX로 자른다. 장부·화면·시뮬이 같은 함수를 쓴다
 */
export function rerollCost(region: RegionNo | number, diff: Difficulty, locks: number, sure: boolean, k: number = REROLL_GOLD_K): RerollCost {
  const r = Math.max(1, Math.min(SHOP_TIER_PRICE_MULT.length, Math.floor(region)));
  const l = Math.max(0, Math.min(REROLL_LOCK_MULT.length - 1, Math.floor(locks)));
  const gold = Math.round(SHOP_PRICE.reroll * SHOP_TIER_PRICE_MULT[r - 1] * DIFFICULTY_LOOT[diff].gold * k * REROLL_LOCK_MULT[l] * (sure ? REROLL_SURE.gold : 1));
  return { gold, dust: REROLL_DUST[l] * (sure ? REROLL_SURE.dust : 1) };
}

/** 마스크의 켜진 비트 수 */
export function popcount(mask: number): number {
  let n = 0, m = mask >>> 0;
  while (m) { n += m & 1; m >>>= 1; }
  return n;
}

/** 재련·잠금이 허용되는 등급(PRD 4.2: 영웅·세트·전설) */
export function isRerollGrade(grade: string): boolean { return grade === 'epic' || grade === 'set' || grade === 'legend'; }
